import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputBase,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import FormulaEditor from 'components/FormulaEditor'
import React, { useEffect, useState } from 'react'
import type { VariableAllocationRow } from 'types/comp-allocations'
import { RowType } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import { currencyFormatter } from 'utils/formatting'

interface AllocationEditDialogProps {
  open: boolean
  onClose: () => void
  row: VariableAllocationRow
  groupLabel: string
  baseSalary: number
  variablePercent: number
  periodProration: number
  rowTargets: ExpandedTarget[]
  onSave: (updates: {
    allocationPercentage: number
    defaultPayout: number
    achievementMultiplier: string
    disabled: boolean
  }) => void
}

const AllocationEditDialog: React.FC<AllocationEditDialogProps> = ({
  open,
  onClose,
  row,
  groupLabel,
  baseSalary,
  variablePercent,
  periodProration,
  rowTargets,
  onSave,
}) => {
  const theme = useTheme()
  const [allocationPercentage, setAllocationPercentage] = useState(row.allocationPercentage)
  const [defaultPayout, setDefaultPayout] = useState(row.defaultPayout)
  const [achievementMultiplier, setAchievementMultiplier] = useState(row.achievementMultiplier)
  const [disabled, setDisabled] = useState(row.disabled ?? false)

  // Local string state for typing — only commits on blur
  const [allocationText, setAllocationText] = useState(((row.allocationPercentage ?? 0) * 100).toFixed(2))
  const [payoutText, setPayoutText] = useState(((row.defaultPayout ?? 0) * 100).toFixed(2))

  // Sync text when arrows change the numeric value
  useEffect(() => {
    setAllocationText(((allocationPercentage ?? 0) * 100).toFixed(2))
  }, [allocationPercentage])
  useEffect(() => {
    setPayoutText(((defaultPayout ?? 0) * 100).toFixed(2))
  }, [defaultPayout])

  const proratedBasePay = baseSalary * periodProration
  const onTargetPayout = proratedBasePay * variablePercent * allocationPercentage
  const maxPayoutDollars = proratedBasePay * variablePercent * allocationPercentage * defaultPayout
  const isDisplayRow = row.rowType === RowType.DISPLAY_ROW
  const isCalculationRow = row.rowType === RowType.CALCULATION_ROW

  const handleSave = () => {
    onSave({ allocationPercentage, defaultPayout, achievementMultiplier, disabled })
    onClose()
  }

  const clampAllocation = (decimal: number) => {
    const min = row.minAllocationPercentage ?? 0
    const max = row.maxAllocationPercentage ?? 1
    return Math.max(min, Math.min(max, Math.round(decimal * 10000) / 10000))
  }

  const clampPayout = (decimal: number) => {
    const min = row.minPayout
    const max = row.maxPayout
    return Math.max(min, Math.min(max, Math.round(decimal * 100) / 100))
  }

  const adjustAllocation = (increment: boolean) => {
    const step = 0.0001
    const next = increment ? allocationPercentage + step : allocationPercentage - step
    setAllocationPercentage(clampAllocation(next))
  }

  const adjustPayout = (increment: boolean) => {
    const step = 0.01
    const next = increment ? defaultPayout + step : defaultPayout - step
    setDefaultPayout(clampPayout(next))
  }

  const adjustMultiplier = (increment: boolean) => {
    const numericPart = Number.parseFloat(achievementMultiplier.replace(/[^\d.-]/g, ''))
    let next = increment ? numericPart + 0.1 : numericPart - 0.1
    next = Math.max(0.01, Math.min(9.99, Math.round(next * 100) / 100))
    setAchievementMultiplier(next.toFixed(2))
  }

  const commitAllocationText = () => {
    const val = Number.parseFloat(allocationText)
    if (!Number.isNaN(val)) {
      const clamped = clampAllocation(val / 100)
      setAllocationPercentage(clamped)
      setAllocationText((clamped * 100).toFixed(2))
    } else {
      setAllocationText(((allocationPercentage ?? 0) * 100).toFixed(2))
    }
  }

  const commitPayoutText = () => {
    const val = Number.parseFloat(payoutText)
    if (!Number.isNaN(val)) {
      const clamped = clampPayout(val / 100)
      setDefaultPayout(clamped)
      setPayoutText((clamped * 100).toFixed(2))
    } else {
      setPayoutText(((defaultPayout ?? 0) * 100).toFixed(2))
    }
  }

  const tileSx = {
    p: 2,
    textAlign: 'center',
    height: 140,
    display: 'flex',
    flexDirection: 'column',
  } as const

  const inputSx = {
    fontSize: '1.1rem',
    textAlign: 'center' as const,
    padding: 0,
  }

  const arrowsSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ml: '2px',
  } as const

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {row.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {groupLabel}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {disabled ? 'Excluded' : 'Included'}
            </Typography>
            <Switch checked={!disabled} onChange={() => setDisabled(!disabled)} size="small" color="primary" />
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent>
        {disabled ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 0.5 }}>
              This allocation is excluded from the plan.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Toggle the switch above to include it.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Allocation % tile */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={tileSx}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                  Allocation %
                </Typography>
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isDisplayRow ? (
                    <Typography style={{ fontSize: '1.1rem' }}>
                      {((allocationPercentage ?? 0) * 100).toFixed(2)}%
                    </Typography>
                  ) : (
                    <>
                      <InputBase
                        value={allocationText}
                        onChange={(e) => setAllocationText(e.target.value)}
                        onBlur={commitAllocationText}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitAllocationText()
                        }}
                        inputProps={{ style: inputSx, inputMode: 'decimal' }}
                        sx={{
                          width: '90px',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          '&:focus-within': { borderBottomColor: theme.palette.primary.main },
                        }}
                        endAdornment={<Typography style={{ fontSize: '1.1rem' }}>%</Typography>}
                      />
                      <Box sx={arrowsSx}>
                        <IconButton
                          size="small"
                          onClick={() => adjustAllocation(true)}
                          disabled={allocationPercentage >= (row.maxAllocationPercentage ?? 1)}
                          sx={{ p: '1px' }}
                        >
                          <ArrowDropUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => adjustAllocation(false)}
                          disabled={allocationPercentage <= (row.minAllocationPercentage ?? 0)}
                          sx={{ p: '1px' }}
                        >
                          <ArrowDropDown fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </div>
                <div style={{ height: '28px' }} />
              </Paper>
            </Grid>

            {/* Max Payout % tile */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={tileSx}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                  Max Payout %
                </Typography>
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isDisplayRow ? (
                    <Typography style={{ fontSize: '1.1rem' }}>{((defaultPayout ?? 0) * 100).toFixed(2)}%</Typography>
                  ) : (
                    <>
                      <InputBase
                        value={payoutText}
                        onChange={(e) => setPayoutText(e.target.value)}
                        onBlur={commitPayoutText}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitPayoutText()
                        }}
                        inputProps={{ style: inputSx, inputMode: 'decimal' }}
                        sx={{
                          width: '90px',
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          '&:focus-within': { borderBottomColor: theme.palette.primary.main },
                        }}
                        endAdornment={<Typography style={{ fontSize: '1.1rem' }}>%</Typography>}
                      />
                      <Box sx={arrowsSx}>
                        <IconButton
                          size="small"
                          onClick={() => adjustPayout(true)}
                          disabled={defaultPayout >= row.maxPayout}
                          sx={{ p: '1px' }}
                        >
                          <ArrowDropUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => adjustPayout(false)}
                          disabled={defaultPayout <= row.minPayout}
                          sx={{ p: '1px' }}
                        >
                          <ArrowDropDown fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </div>
                <div style={{ height: '28px' }} />
              </Paper>
            </Grid>

            {/* Accelerator tile */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={tileSx}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                  Accelerator
                </Typography>
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography style={{ fontSize: '1.1rem' }}>
                    {achievementMultiplier.replace(/[^\d.-]/g, '')}x
                  </Typography>
                  {isCalculationRow && (
                    <Box sx={arrowsSx}>
                      <IconButton
                        size="small"
                        onClick={() => adjustMultiplier(true)}
                        disabled={Number.parseFloat(achievementMultiplier.replace(/[^\d.-]/g, '')) >= 9.9}
                        sx={{ p: '1px' }}
                      >
                        <ArrowDropUp fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => adjustMultiplier(false)}
                        disabled={Number.parseFloat(achievementMultiplier.replace(/[^\d.-]/g, '')) <= 0.1}
                        sx={{ p: '1px' }}
                      >
                        <ArrowDropDown fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </div>
                <div style={{ height: '28px' }} />
              </Paper>
            </Grid>

            {/* On-Target Payout */}
            {!isDisplayRow && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    On-Target Payout
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ mt: 0.5 }}>
                    {currencyFormatter.format(onTargetPayout)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Up to {currencyFormatter.format(maxPayoutDollars)} max
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Formula Editor */}
            {rowTargets.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Formula
                </Typography>
                <FormulaEditor
                  handleFormulaChange={() => {}}
                  selectedTargets={rowTargets}
                  currentRequirements={row.formulaRequirements ?? []}
                  currentOptions={row.formulaOptions ?? []}
                  theme={theme}
                  rowType={row.rowType}
                  isReviewMode
                />
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AllocationEditDialog
